import { ParallaxImage } from './ParallaxImage';

export const ParallaxType = {
  Tile: 'tile',
  Sprite: 'sprite'
} as const;
type ParallaxTypeKeys = keyof typeof ParallaxType;
type ParallaxTypeValues = typeof ParallaxType[ParallaxTypeKeys];

export type ParallaxData = ParallaxTileData | ParallaxSpriteData;

export const ParallaxDirection = {
  Horizontal: 'horizontal',
  Vertical: 'vertical'
} as const;
type ParallaxDirectionKeys = keyof typeof ParallaxDirection;
type ParallaxDirectionValues = typeof ParallaxDirection[ParallaxDirectionKeys];

// export interface ParallaxData {
//   data: ParallaxTileData | ParallaxSpriteData;
// }

export interface ParallaxTileData {
  type: typeof ParallaxType.Tile;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  scale?: number;
  texture: string;
  frame: string;
  depth: number;
  scrollX: number;
  scrollY: number;
  speedX: number;
  speedY: number;
  naturalDriftX: number;
  naturalDriftY: number;
}

export interface ParallaxSpriteData {
  type: typeof ParallaxType.Sprite;
  direction: ParallaxDirectionValues;
  x: number;
  y: number;
  texture: string;
  images: ParallaxSpriteImageData[];
  depth: number;
  scrollX: number;
  scrollY: number;
  speedX: number;
  speedY: number;
  naturalDriftX: number;
  naturalDriftY: number;
}

export interface ParallaxSpriteImageData {
  texture?: string;
  frame: string;
  scale?: number;
  gapBefore?: number;
  gapAfter?: number;
}

export interface ParallaxTileLayer {
  sprite: Phaser.GameObjects.TileSprite;
  speedX: number;
  speedY: number;
  naturalDriftX: number;
  naturalDriftY: number;
  driftX: number;
  driftY: number;
}

export interface ParallaxSpriteLayer {
  direction: ParallaxDirectionValues;
  images: ParallaxImage[];
  speedX: number;
  speedY: number;
  naturalDriftX: number;
  naturalDriftY: number;
  driftX: number;
  driftY: number;
  width: number;
  height: number;
}

export class ParallaxController {
  protected scene: Phaser.Scene;
  protected camera: Phaser.Cameras.Scene2D.Camera;
  protected layer: Phaser.GameObjects.Layer;
  protected isFrozen: boolean;
  protected parallaxTileLayers: ParallaxTileLayer[];
  protected parallaxSpriteLayers: ParallaxSpriteLayer[];
  protected cameraOffset: Phaser.Geom.Point;

  constructor(scene: Phaser.Scene, camera: Phaser.Cameras.Scene2D.Camera, parallaxLayer: Phaser.GameObjects.Layer) {
    this.scene = scene;
    this.camera = camera;
    this.layer = parallaxLayer;

    this.isFrozen = false;
    this.parallaxTileLayers = [];
    this.parallaxSpriteLayers = [];

    this.cameraOffset = new Phaser.Geom.Point(0, 0);

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  public buildLayersFromData(parallaxLayers: ParallaxData[]) {
    parallaxLayers.forEach((layer) => {
      if (layer.type == ParallaxType.Tile) {
        if (layer.x != null && layer.y != null && layer.width != null && layer.height != null) {
          // this.addTileLayer(layer as Required<ParallaxTileData>, false);
        } else {
          this.addFullscreenTileLayer(layer);
        }
      } else if (layer.type == ParallaxType.Sprite) {
        this.addSpriteLayer(layer as ParallaxSpriteData);
      }
    });

    // Debug lines.
    // let rec = this.scene.add.rectangle(-200, 768, 2350, 2, 0xff0000);
    // rec.setOrigin(0, 0.5);
    // rec.setScrollFactor(0, 0);

    // let rec1 = this.scene.add.rectangle(-200, 570, 2350, 2, 0xff0000);
    // rec1.setOrigin(0, 0.5);
    // rec1.setScrollFactor(0, 0);

    // let rec2 = this.scene.add.rectangle(-200, 570 * 1.25, 2350, 2, 0xff0000);
    // rec2.setOrigin(0, 0.5);
    // rec2.setScrollFactor(0, 0);

    // let rec3 = this.scene.add.rectangle(-200, 158, 2350, 2, 0xffff00);
    // rec3.setOrigin(0, 0.5);
    // rec3.setScrollFactor(0, 0);

    // let rec4 = this.scene.add.rectangle(-200, 158 + 39.375, 2350, 2, 0xffff00);
    // rec4.setOrigin(0, 0.5);
    // rec4.setScrollFactor(0, 0);
  }

  public addFullscreenTileLayer(layerData: ParallaxTileData) {
    layerData.x = this.camera.x + this.camera.centerX;
    layerData.y = this.camera.y + this.camera.centerY;
    layerData.width = this.camera.width;
    layerData.height = this.camera.height;

    // this.addTileLayer(layerData as Required<ParallaxTileData>, true);
  }

  public addTileLayer(layerData: Required<ParallaxTileData>, isFullscreen: boolean) {
    let y = layerData.y;
    if (layerData.scrollY == 0 && !isFullscreen) y = y * (1 / this.camera.zoomX);

    let parallaxLayer = this.scene.make.tileSprite({
      x: layerData.x,
      y: y,
      // y: isFullscreen ? layerData.y : layerData.y * (1 / this.camera.zoomX),
      width: layerData.width,
      height: layerData.height,
      key: layerData.texture,
      frame: layerData.frame,
      depth: layerData.depth,
      scale: 1 / this.camera.zoomX
    });
    console.log('Parallax', layerData.frame, parallaxLayer.depth);

    // Tilesprite origins don't change with scale, so we need to offset any where they don't match.
    if (layerData.width < this.camera.width && layerData.scrollX == 0)
      parallaxLayer.x = parallaxLayer.x - (parallaxLayer.displayWidth / 2 - parallaxLayer.displayOriginX) * 2;
    if (layerData.height < this.camera.height && layerData.scrollY == 0)
      parallaxLayer.y = parallaxLayer.y - (parallaxLayer.displayHeight / 2 - parallaxLayer.displayOriginY) * 2;

    parallaxLayer.setScrollFactor(layerData.scrollX, layerData.scrollY);

    this.parallaxTileLayers.push({
      sprite: parallaxLayer,
      speedX: layerData.speedX,
      speedY: layerData.speedY,
      naturalDriftX: layerData.naturalDriftX,
      naturalDriftY: layerData.naturalDriftY,
      driftX: 0,
      driftY: 0
    });

    let layerItem = this.layer.add(parallaxLayer);
    // }
  }

  public addSpriteLayer(layerData: ParallaxSpriteData) {
    // let offsetX = layerData.x * (1 / this.camera.zoomX);
    // let offsetY = layerData.y * (1 / this.camera.zoomY);
    let offsetX = layerData.x;
    let offsetY = layerData.y;

    let width = 0;
    let images: ParallaxImage[] = [];
    for (const imageData of layerData.images) {
      let image = new ParallaxImage(
        this.scene,
        layerData.direction == ParallaxDirection.Horizontal ? offsetX + (imageData.gapBefore ?? 0) : offsetX,
        layerData.direction == ParallaxDirection.Vertical ? offsetY + (imageData.gapBefore ?? 0) : offsetY,
        layerData.direction == ParallaxDirection.Horizontal ? offsetX + (imageData.gapAfter ?? 0) : offsetX,
        layerData.direction == ParallaxDirection.Vertical ? offsetY + (imageData.gapAfter ?? 0) : offsetY,
        imageData.texture || layerData.texture,
        imageData.frame
      );
      image.originX = 0;
      image.setScale(1 / this.camera.zoom);
      image.updateDisplayOrigin();
      image.setScrollFactor(layerData.scrollX, layerData.scrollY);
      image.setDepth(layerData.depth);
      images.push(image);
      this.layer.add(image);

      if (layerData.direction == ParallaxDirection.Horizontal) {
        offsetX += image.displayWidth + (imageData.gapBefore ?? 0) + (imageData.gapAfter ?? 0);
      } else if (layerData.direction == ParallaxDirection.Vertical) {
        offsetY += image.displayHeight + (imageData.gapBefore ?? 0) + (imageData.gapAfter ?? 0);
      }
    }

    this.parallaxSpriteLayers.push({
      direction: layerData.direction,
      images: images,
      speedX: layerData.speedX * (1 / this.camera.zoomX),
      speedY: layerData.speedY * (1 / this.camera.zoomY),
      naturalDriftX: layerData.naturalDriftX * (1 / this.camera.zoomX),
      naturalDriftY: layerData.naturalDriftY * (1 / this.camera.zoomY),
      driftX: 0,
      driftY: 0,
      width: offsetX - layerData.x,
      height: offsetY - layerData.y
    });
  }

  public clearLayers() {
    // this.layer.removeAll();

    for (const layer of this.parallaxTileLayers) {
      layer.sprite.destroy();
    }

    // this.parallaxTileLayers.forEach((layer) => {
    //   layer.sprite.destroy();
    // });

    for (const layer of this.parallaxSpriteLayers) {
      for (const image of layer.images) {
        image.destroy();
      }

      layer.images = [];
    }

    this.parallaxTileLayers = [];
    this.parallaxSpriteLayers = [];
  }

  public freezePosition() {
    this.isFrozen = true;
    this.cameraOffset.x += this.camera.scrollX;
    this.cameraOffset.y += this.camera.scrollY;
    console.log(`Parallax freezing at ${this.cameraOffset.x}, ${this.cameraOffset.y}`);
  }

  public unfreezePosition() {
    this.isFrozen = false;
    this.cameraOffset.x -= this.camera.scrollX;
    this.cameraOffset.y -= this.camera.scrollY;
    console.log(`Parallax unfreezing at ${this.cameraOffset.x}, ${this.cameraOffset.y}`);
  }

  protected update(time: number, delta: number) {
    if (this.isFrozen) return;

    for (const layer of this.parallaxTileLayers) {
      layer.driftX += layer.naturalDriftX;
      layer.driftY += layer.naturalDriftY;

      layer.sprite.tilePositionX = layer.driftX + (this.camera.scrollX + this.cameraOffset.x) * layer.speedX;
      layer.sprite.tilePositionY = layer.driftY + (this.camera.scrollY + this.cameraOffset.y) * layer.speedY;
    }

    for (const layer of this.parallaxSpriteLayers) {
      layer.driftX -= layer.naturalDriftX;
      layer.driftY -= layer.naturalDriftY;

      for (const image of layer.images) {
        // Correct for incorrect origin point in either the tileSprites or in the images. One of them is wrong, not
        // sure which, but this brings the images into line with the tileSprites.
        // let originXCorrection = (image.displayWidth * 0.5 - image.displayOriginX);
        // let originYCorrection = (image.displayHeight * 0.5 - image.displayOriginY);

        image.x =
          layer.driftX + (this.camera.scrollX + this.cameraOffset.x) * -layer.speedX + image.OffsetX;
        image.y =
          layer.driftY + (this.camera.scrollY + this.cameraOffset.y) * -layer.speedY + image.OffsetY;
        // image.x = (this.camera.scrollX + this.cameraOffset.x) * -layer.speedX + image.OffsetX;

        if (layer.direction == ParallaxDirection.Horizontal) {
          if (image.x + image.displayWidth < this.camera.x - 240) {
            let wrapPos =
              this.camera.displayWidth + image.displayWidth > layer.width
                ? this.camera.displayWidth + image.displayWidth
                : layer.width;
            image.resetX(wrapPos);
          }
        }
      }
    }
  }

  protected shutdown() {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
  }
}
